import { colors, styled } from 'assets/theme';
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';

const CIQ = (window as any).CIQ

export interface IPeriodicityParameters {
  period: number,
  timeUnit?: "millisecond" | "second" | "minute" | "hour" | "day" | "week" | "month" | "tick"
  interval?: number
}

interface ISetRangeParameters {
  dtLeft?: Date,
  dtRight?: Date,
  padding?: number
  chart?: any
  goIntoFuture?: boolean,
  goIntoPast?: boolean,
  periodicity?: IPeriodicityParameters,
  pixelPerBar?: number
  dontSaveRangeToLayout?: boolean,
  forceLoad?: boolean
}

interface IDateSelectorProps {
  ciq: any,
  enableDateFilter: boolean,
  setEnableDateFilter: (enabled: boolean) => void
  minDate: Date | undefined
}

export const DateSelector: React.FunctionComponent<IDateSelectorProps> = ({ ciq, minDate, enableDateFilter, setEnableDateFilter }) => {
  // const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentDtLeft, setCurrentDtLeft] = useState<Date>()
  const [currentDtRight, setCurrentDtRight] = useState<Date>()
  const [lookbackMode, setLookbackMode] = useState(false)
  const dtLeftRef = useRef<DatePicker>(null);
  const dtRightRef = useRef<DatePicker>(null);

  useEffect(() => {
    if (ciq) {
      ciq.highlandLookbackMode = lookbackMode
    }
  }, [ciq, lookbackMode])

  useEffect(() => {
    if (ciq) {
      CIQ.ChartEngine.prototype.append("createDataSegment", () => {
        if (!ciq.hqifRangeChanging) {
          const dataSegment = ciq.chart.dataSegment as any[];
          if (dataSegment && dataSegment.length > 0) {
            if (dataSegment[0]) {
              setCurrentDtLeft(dataSegment[0].DT as Date)
            }
            if (dataSegment[dataSegment.length - 1]) {
              setCurrentDtRight(dataSegment[dataSegment.length - 1].DT as Date)
            }
          }
        }
    });
    CIQ.ChartEngine.prototype.prepend("clearCurrentMarketData", () => {
      if (ciq.layout.range && ciq.layout.range.dtRight) {
        setLookbackMode(true)
      } else {
        setLookbackMode(false)
      }
    });
    }
  }, [ciq])

  const updateRange = (dtLeft: Date, dtRight: Date) => {
    ciq.hqifRangeChanging = true
    setCurrentDtLeft(dtLeft)
    setCurrentDtRight(dtRight)
    const periodicity: IPeriodicityParameters = {
      interval: 1,
      period: 1,
      timeUnit: 'second',
    };

    if (ciq.layout.interval === 'tick') {
      periodicity.period = ciq.layout.periodicity;
      periodicity.interval = ciq.layout.interval;
      periodicity.timeUnit = 'tick';
    } else if (ciq.layout.timeUnit === 'minute') {
      periodicity.period = ciq.layout.periodicity;
      periodicity.interval = ciq.layout.interval;
      periodicity.timeUnit = 'minute';
    } else if (ciq.layout.timeUnit === 'second') {
      periodicity.period = ciq.layout.periodicity;
      periodicity.interval = ciq.layout.interval;
      periodicity.timeUnit = 'second';
    } else if (ciq.layout.interval === 'hour') {
      periodicity.period = ciq.layout.periodicity;
      periodicity.timeUnit = 'hour';
    } else {
      periodicity.period = ciq.layout.periodicity;
      periodicity.timeUnit = 'day';
    }
    ciq.setRange({
      dtLeft,
      dtRight,
      forceLoad: true,
      periodicity,
    } as ISetRangeParameters);
    ciq.draw();
    ciq.hqifRangeChanging = false
  }

  const handleLeftChange = (date: Date) => {
    if (currentDtLeft && currentDtRight) {
      const diff = currentDtRight.getTime() - currentDtLeft.getTime()
      const dtLeft = new Date(date);
      const dtRight = new Date(date.getTime() + diff);
      updateRange(dtLeft, dtRight)
    }
  }

  const handleRightChange = (date: Date) => {
    if (currentDtLeft && currentDtRight) {
      const diff = currentDtRight.getTime() - currentDtLeft.getTime()
      const dtLeft = new Date(date.getTime() - diff);
      const dtRight = new Date(date);
      updateRange(dtLeft, dtRight)
    }
  }

  const handleDisabledChange = (e: any) => {
    if (lookbackMode) {
      delete ciq.layout.range;
      ciq.isHistoricalModeSet=false; // turn off historical mode before anything else is done
      ciq.loadChart(ciq.chart.symbol); // no need to call home in loadChart
    } else {
      setLookbackMode(lbm => !lbm)
    }
  }

  return (
    <div style={{ display: 'grid', justifyContent: 'center'}}>
      <DatePickerWrapper>
        <DatePicker
          ref={dtLeftRef}
          todayButton="Today"
          showTimeSelect={true}
          timeFormat="HH:mm"
          timeIntervals={15}
          timeCaption="time"
          dateFormat="MMMM d, yyyy h:mm aa"
          minDate={minDate}
          maxDate={new Date()}
          selected={currentDtLeft}
          onChange={handleLeftChange}
          disabled={!lookbackMode}
        />
        <DatePicker
          ref={dtRightRef}
          todayButton="Today"
          showTimeSelect={true}
          timeFormat="HH:mm"
          timeIntervals={15}
          timeCaption="time"
          dateFormat="MMMM d, yyyy h:mm aa"
          minDate={minDate}
          maxDate={new Date()}
          selected={currentDtRight}
          onChange={handleRightChange}
          disabled={!lookbackMode}
        />
        {
          lookbackMode ?
            <ModeButton accent={colors.accents.dominant} onClick={handleDisabledChange}>Lookback</ModeButton>
            :
            <ModeButton accent={colors.accents.good} onClick={handleDisabledChange}>Live</ModeButton>
        }
      </DatePickerWrapper>
    </div>
  );
};

const DatePickerWrapper = styled.span`
  display: grid;
  grid-auto-flow: column;
  justify-content: space-between;
  grid-gap:1em;
  .react-datepicker__input-container {
    background-color: initial;
    color: ${({ theme }) => theme.core.textColor};
    border-color: #d9d9d9 #ccc #b3b3b3;
    border: 1px solid #ccc;
    cursor: default;
    display: table;
    border-spacing: 0;
    border-collapse: separate;
    height: 36px;
    outline: none;
    overflow: hidden;
    position: relative;
    width: 240px;
  }

  input[type="text"], input[type="number"] {
    border: none;
    padding: 0px 10px 0px 10px;
    line-height: 34px;
    width: 100%
  }

  .react-datepicker-time__input-container {
    display: inline-block;
    .react-datepicker-time__input {
      display: inline-block;
      margin-left: 10px;
      input {
        width: 85px;
      }
      input[type="time"]::-webkit-inner-spin-button,
      input[type="time"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      input[type="time"] {
        -moz-appearance: textfield;
      }
    }
    .react-datepicker-time__delimiter {
      margin-left: 5px;
      display: inline-block;
    }
  }
  .react-datepicker__triangle {
  }

  .react-datepicker__header {
  }

  .react-datepicker__month-container {
  }

  .react-datepicker-popper {
    z-index: 3;
  }

  .react-datepicker__day--disabled, .react-datepicker__month-text--disabled, .react-datepicker__quarter-text--disabled {
    cursor: default;
    color: #ccc;
    background-color: black;
  }
  .react-datepicker__day--disabled:hover, .react-datepicker__month-text--disabled:hover {
    background-color: black;
  }
`

const ModeButton = styled.button<{ accent?:any}>`
  border: none;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100px;
  border-radius:4px;
  background-color: ${({ accent }) => accent ? accent.base : 'initial'};
  &:focus {
    background-color: ${({ accent }) => accent ? accent.darker : 'initial'};
    outline: none;
  }

  :hover {
    cursor: pointer;
    color: black;
    background-color: ${({ accent }) => accent ? accent.darker : 'initial'};
  }
  :disabled {
    background-color: gray;
  }
`;